/**
 * Centers API — mirrors `cargoCenters.js`. Plus the center-specific
 * shipment phases (intake / handoff / refuse / cash-event) on the
 * shipments router.
 */
import { api } from './client';
import type { Center, CenterRating, CenterOperator } from '@/types/center';

export interface CreateCenterInput {
  name: string;
  address: string;
  city: string;
  country: string;
  cityId?: number;
  lat: number;
  lng: number;
  phone?: string;
  email?: string;
  operatingHours?: string | Record<string, string>;
  is_24_hours?: boolean;
  notes?: string;
  hasRefrigeration?: boolean;
  maxStorageWeight?: number;
  storefrontPhotoUrl?: string;
  exteriorPhotoUrl?: string;
  interiorPhotoUrl?: string;
  surroundingsPhotoUrl?: string;
}

function unwrap<T>(res: { data?: any }): T {
  return (res.data?.data ?? res.data) as T;
}

export const centersApi = {
  async listApproved(): Promise<Center[]> {
    const r = await api.get(`/api/cargo/centers`);
    return unwrap<Center[]>(r);
  },
  async listMy(ownerOnly = false): Promise<Center[]> {
    const r = await api.get(`/api/cargo/centers/my`, { params: { ownerOnly } });
    return unwrap<Center[]>(r);
  },
  async findNearest(opts: { lat: number; lng: number; city?: string; radiusKm?: number; limit?: number }): Promise<Center[]> {
    const r = await api.get(`/api/cargo/centers/nearest`, { params: opts });
    return unwrap<Center[]>(r);
  },
  async findById(id: number): Promise<Center> {
    const r = await api.get(`/api/cargo/centers/${id}`);
    return unwrap<Center>(r);
  },
  async create(input: CreateCenterInput): Promise<Center> {
    const r = await api.post(`/api/cargo/centers`, input);
    return unwrap<Center>(r);
  },
  async update(id: number, input: Partial<CreateCenterInput>): Promise<Center> {
    const r = await api.put(`/api/cargo/centers/${id}`, input);
    return unwrap<Center>(r);
  },
  async deactivate(id: number): Promise<void> {
    await api.delete(`/api/cargo/centers/${id}`);
  },
  async pause(id: number, until: string, reason?: string): Promise<void> {
    await api.put(`/api/cargo/centers/${id}/pause`, { until, reason });
  },
  async resume(id: number): Promise<void> {
    await api.delete(`/api/cargo/centers/${id}/pause`);
  },

  // Ratings
  async listRatings(id: number): Promise<CenterRating[]> {
    const r = await api.get(`/api/cargo/centers/${id}/ratings`);
    return unwrap<CenterRating[]>(r);
  },

  // Operators
  async listOperators(id: number): Promise<CenterOperator[]> {
    const r = await api.get(`/api/cargo/centers/${id}/operators`);
    return unwrap<CenterOperator[]>(r);
  },
  async inviteOperator(id: number, payload: { userId?: string; phone?: string }): Promise<void> {
    await api.post(`/api/cargo/centers/${id}/operators`, payload);
  },
  async revokeOperator(id: number, userId: string): Promise<void> {
    await api.delete(`/api/cargo/centers/${id}/operators/${encodeURIComponent(userId)}`);
  },
  async myInvites(): Promise<unknown[]> {
    const r = await api.get(`/api/cargo/centers/operators/my-invites`);
    return unwrap<unknown[]>(r);
  },
  async acceptInvite(id: number): Promise<void> {
    await api.post(`/api/cargo/centers/${id}/operators/accept`);
  },

  // Center-side shipment phases
  async intakeShipment(shipmentId: number, opts: {
    photoUrl?: string; actualWeight?: number; shelfLocation?: string;
  }): Promise<void> {
    await api.post(`/api/cargo/shipments/${shipmentId}/center-intake`, opts);
  },
  async handoffShipment(shipmentId: number, opts: { target: 'driver' | 'receiver'; otpCode?: string }): Promise<void> {
    await api.post(`/api/cargo/shipments/${shipmentId}/center-handoff`, opts);
  },
  async refuseShipment(shipmentId: number, reason?: string): Promise<void> {
    await api.post(`/api/cargo/shipments/${shipmentId}/center-refuse`, { reason });
  },
};
